import { useCallback, useRef } from 'react';
import type { ChangeEventHandler, ReactNode } from 'react';
import { Switch } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { tss } from 'tss-react/mui';
import { Label } from '../Label';

const useStyles = tss.create(({ theme }) => ({
    root: {
        display: 'inline-block', // inline-flex is wierd, but probably preferable.
    },
    label: {
        cursor: 'pointer',
        color: theme.bino.color.gray,
    },
    switch: {
        verticalAlign: 'middle',
    },
    active: {
        color: theme.bino.color.black,
    },
}));

type DualLabelSwitchProps = {
    inactiveLabel?: ReactNode;
    activeLabel?: ReactNode;
    checked: boolean;
    onChange: ChangeEventHandler;
    className?: string;
};

export default function DualLabelSwitch({
    inactiveLabel,
    activeLabel,
    checked,
    onChange,
    className,
    ...props
}: DualLabelSwitchProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { classes, cx } = useStyles();

    const handleInactivate = useCallback(() => {
        if (inputRef.current?.checked) {
            inputRef.current.click();
        }
    }, []);

    const handleActivate = useCallback(() => {
        if (inputRef.current && !inputRef.current.checked) {
            inputRef.current.click();
        }
    }, []);
    return (
        <div className={cx(classes.root, className)}>
            <Label
                onClick={handleInactivate}
                className={cx(classes.label, {
                    [classes.active]: checked === false,
                })}
            >
                {inactiveLabel || (
                    <FormattedMessage
                        defaultMessage="Inactive"
                        description="Label for an inactive switch"
                    />
                )}
            </Label>
            <Switch
                {...props}
                color="primary"
                inputRef={inputRef}
                className={classes.switch}
                checked={checked}
                onChange={onChange}
            />
            <Label
                onClick={handleActivate}
                className={cx(classes.label, {
                    [classes.active]: checked === true,
                })}
            >
                {activeLabel || (
                    <FormattedMessage
                        defaultMessage="Active"
                        description="Label for an active switch"
                    />
                )}
            </Label>
        </div>
    );
}
